import { uniqId } from '@utils/id';
export default class ComboboxWidget extends EventTarget {
    constructor(element, options = {}) {
        var _a;
        super();
        this.items = [];
        this.item = null;
        this.firstItem = null;
        this.lastItem = null;
        this.filteredItems = [];
        this.filter = '';
        this.autocomplete = 'none';
        this.inputHasVisualFocus = false;
        this.listboxHasVisualFocus = false;
        this.hasHover = false;
        this.closing = false;
        if (!element)
            throw new Error('undefined element');
        this.element = element;
        this.options = Object.assign(Object.assign({}, ComboboxWidget.defaults()), options);
        const input = this.element.querySelector('input[role="combobox"]');
        if (!input)
            throw new Error('undefined input');
        this.input = input;
        const listbox = this.element.querySelector(`.${this.options.baseClass}__listbox`);
        if (!listbox)
            throw new Error('undefined listbox');
        this.listbox = listbox;
        this.button = (_a = this.element.querySelector(`.${this.options.baseClass}__button`)) !== null && _a !== void 0 ? _a : null;
        this.init();
    }
    static defaults() {
        return {
            baseClass: 'combobox',
        };
    }
    init() {
        var _a, _b, _c;
        if (this.input.id === '')
            this.input.id = uniqId('combobox-');
        let controlsId = this.input.getAttribute('aria-controls');
        if (!controlsId) {
            controlsId = `${this.input.id}-listbox`;
            this.input.setAttribute('aria-controls', controlsId);
        }
        this.listbox.id = controlsId;
        (_a = this.button) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-controls', controlsId);
        if (this.button) {
            this.button.setAttribute('tabindex', '-1');
            this.button.setAttribute('aria-expanded', 'false');
        }
        this.autocomplete = ((_b = this.input.getAttribute('aria-autocomplete')) === null || _b === void 0 ? void 0 : _b.toLowerCase()) || 'none';
        this.input.addEventListener('keydown', this.handleInputKeyDown.bind(this));
        this.input.addEventListener('keyup', this.handleInputKeyUp.bind(this));
        this.input.addEventListener('click', this.handleInputClick.bind(this));
        this.input.addEventListener('focus', this.handleInputFocus.bind(this));
        this.input.addEventListener('blur', this.handleInputBlur.bind(this));
        document.body.addEventListener('pointerup', this.handleBackgroundPointerUp.bind(this), true);
        this.listbox.addEventListener('pointerover', this.handleListboxPointerover.bind(this));
        this.listbox.addEventListener('pointerout', this.handleListboxPointerout.bind(this));
        this.items = Array.from(this.listbox.querySelectorAll(`.${this.options.baseClass}__option`));
        this.items.forEach((item) => {
            item.setAttribute('role', 'option');
            if (!item.id)
                item.id = uniqId('combobox-option-');
            item.addEventListener('click', this.handleItemClick.bind(this));
            item.addEventListener('pointerover', this.handleItemPointerover.bind(this));
            item.addEventListener('pointerout', this.handleItemPointerout.bind(this));
        });
        (_c = this.button) === null || _c === void 0 ? void 0 : _c.addEventListener('click', this.handleButtonClick.bind(this));
        this.filterItems();
    }
    static isItemInView(item) {
        const bounding = item.getBoundingClientRect();
        return (bounding.top >= 0 &&
            bounding.left >= 0 &&
            bounding.bottom <=
                (window.innerHeight || document.documentElement.clientHeight) &&
            bounding.right <=
                (window.innerWidth || document.documentElement.clientWidth));
    }
    setActiveDescendant(item = null) {
        if (item && this.listboxHasVisualFocus) {
            this.input.setAttribute('aria-activedescendant', item.id);
            if (!ComboboxWidget.isItemInView(item)) {
                item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
        else {
            this.input.setAttribute('aria-activedescendant', '');
        }
    }
    filterItems() {
        if (this.autocomplete === 'none') {
            this.filter = '';
        }
        let item = null;
        const currentItem = this.item;
        const filter = this.filter.toLowerCase();
        this.filteredItems = [];
        this.input.innerHTML = '';
        this.items.forEach((item) => {
            if (filter.length === 0 ||
                item.innerText.toLowerCase().indexOf(filter) === 0) {
                this.filteredItems.push(item);
                this.listbox.appendChild(item);
            }
        });
        // Use populated options array to initialize firstOption and lastOption.
        const numItems = this.filteredItems.length;
        if (numItems > 0) {
            this.firstItem = this.filteredItems[0];
            this.lastItem = this.filteredItems[numItems - 1];
            item = (currentItem && this.filteredItems.indexOf(currentItem) >= 0)
                ? currentItem
                : this.firstItem;
        }
        else {
            this.firstItem = null;
            item = null;
            this.lastItem = null;
        }
        return item;
    }
    setValue(value) {
        this.filter = value;
        this.input.value = this.filter;
        this.input.dispatchEvent(new Event('change'));
        this.input.dispatchEvent(new Event('input'));
        this.input.setSelectionRange(this.filter.length, this.filter.length);
        this.filterItems();
    }
    setCurrentOptionStyle(item = null) {
        this.filteredItems.forEach((opt) => {
            if (opt === item) {
                opt.setAttribute('aria-selected', 'true');
                if (this.listbox.scrollTop + this.listbox.offsetHeight <
                    opt.offsetTop + opt.offsetHeight) {
                    this.listbox.scrollTop =
                        opt.offsetTop + opt.offsetHeight - this.listbox.offsetHeight;
                }
                else if (this.listbox.scrollTop > opt.offsetTop + 2) {
                    this.listbox.scrollTop = opt.offsetTop;
                }
            }
            else {
                opt.removeAttribute('aria-selected');
            }
        });
    }
    setItem(item, flag = false) {
        if (item) {
            this.item = item;
            this.setCurrentOptionStyle(this.item);
            this.setActiveDescendant(this.item);
            if (this.autocomplete === 'both') {
                this.input.value = this.item.textContent;
                if (flag) {
                    this.input.setSelectionRange(this.item.textContent.length, this.item.textContent.length);
                }
                else {
                    this.input.setSelectionRange(this.filter.length, this.item.textContent.length);
                }
            }
        }
    }
    setVisualFocusInput() {
        this.listbox.classList.remove('focus');
        this.input.classList.add('focus');
        this.inputHasVisualFocus = true;
        this.listboxHasVisualFocus = false;
        this.setActiveDescendant(null);
    }
    setVisualFocusListbox() {
        this.input.classList.remove('focus');
        this.inputHasVisualFocus = false;
        this.listboxHasVisualFocus = true;
        this.listbox.classList.add('focus');
        this.setActiveDescendant(this.item);
    }
    removeVisualFocusAll() {
        this.input.classList.remove('focus');
        this.inputHasVisualFocus = false;
        this.listboxHasVisualFocus = false;
        this.listbox.classList.remove('focus');
        this.item = null;
        this.setActiveDescendant(null);
    }
    getPreviousItem(currentItem) {
        if (!currentItem)
            return null;
        if (currentItem !== this.firstItem) {
            const index = this.filteredItems.indexOf(currentItem);
            return this.filteredItems[index - 1];
        }
        return this.lastItem;
    }
    getNextItem(currentItem) {
        if (!currentItem)
            return null;
        if (currentItem !== this.lastItem) {
            const index = this.filteredItems.indexOf(currentItem);
            return this.filteredItems[index + 1];
        }
        return this.firstItem;
    }
    doesItemHaveFocus() {
        return this.input.getAttribute('aria-activedescendant') !== '';
    }
    isOpen() {
        return this.listbox.classList.contains('is-open');
    }
    isClosed() {
        return !this.listbox.classList.contains('is-open');
    }
    hasOptions() {
        return this.filteredItems.length;
    }
    open() {
        var _a;
        this.listbox.classList.add('is-open');
        this.input.setAttribute('aria-expanded', 'true');
        (_a = this.button) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'true');
    }
    close(force = false) {
        var _a;
        if (force ||
            (!this.inputHasVisualFocus &&
                !this.listboxHasVisualFocus &&
                !this.hasHover)) {
            this.setCurrentOptionStyle(null);
            this.listbox.classList.remove('is-open');
            this.input.setAttribute('aria-expanded', 'false');
            (_a = this.button) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'false');
            this.setActiveDescendant(null);
            // this.input.classList.add('focus');
            this.closing = true;
            setTimeout(() => {
                this.closing = false;
            }, 300);
        }
    }
    handleInputKeyDown(event) {
        var _a, _b, _c, _d;
        let flag = false;
        const altKey = event.altKey;
        if (event.ctrlKey || event.shiftKey) {
            return;
        }
        const inputLength = (_b = (_a = this.input.value) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
        switch (event.key) {
            case 'Enter':
                if (this.listboxHasVisualFocus) {
                    this.setValue((_d = (_c = this.item) === null || _c === void 0 ? void 0 : _c.textContent) !== null && _d !== void 0 ? _d : '');
                }
                this.close(true);
                this.setVisualFocusInput();
                flag = true;
                break;
            case 'Down':
            case 'ArrowDown':
                if (this.filteredItems.length > 0) {
                    if (altKey) {
                        this.open();
                    }
                    else {
                        this.open();
                        if (this.listboxHasVisualFocus ||
                            (this.autocomplete === 'both' && this.filteredItems.length > 1)) {
                            this.setItem(this.getNextItem(this.item), true);
                            this.setVisualFocusListbox();
                        }
                        else {
                            this.setItem(this.firstItem, true);
                            this.setVisualFocusListbox();
                        }
                    }
                }
                flag = true;
                break;
            case 'Up':
            case 'ArrowUp':
                if (this.hasOptions()) {
                    if (this.listboxHasVisualFocus) {
                        this.setItem(this.getPreviousItem(this.item), true);
                    }
                    else {
                        this.open();
                        if (!altKey) {
                            this.setItem(this.lastItem, true);
                            this.setVisualFocusListbox();
                        }
                    }
                }
                flag = true;
                break;
            case 'Esc':
            case 'Escape':
                if (this.isOpen()) {
                    this.close(true);
                    this.filter = this.input.value;
                    this.filterItems();
                    this.setVisualFocusInput();
                }
                else {
                    this.setValue('');
                    this.input.value = '';
                }
                this.item = null;
                flag = true;
                break;
            case 'Tab':
                this.close(true);
                if (this.listboxHasVisualFocus) {
                    if (this.item) {
                        this.setValue(this.item.textContent);
                    }
                }
                break;
            case 'Home':
                this.input.setSelectionRange(0, 0);
                flag = true;
                break;
            case 'End':
                this.input.setSelectionRange(inputLength, inputLength);
                flag = true;
                break;
            default:
                break;
        }
        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
    static isPrintableCharacter(str) {
        return str.length === 1 && Boolean(str.match(/\S| /));
    }
    // Input Events
    handleInputKeyUp(event) {
        var _a, _b;
        let flag = false;
        let item = null;
        const char = event.key;
        if (ComboboxWidget.isPrintableCharacter(char)) {
            this.filter += char;
        }
        // this is for the case when a selection in the textbox has been deleted
        if (this.input.value.length < this.filter.length) {
            this.filter = this.input.value;
            this.item = null;
            this.filterItems();
        }
        if (event.key === 'Escape' || event.key === 'Esc') {
            return;
        }
        switch (event.key) {
            case 'Backspace':
                this.setVisualFocusInput();
                this.setCurrentOptionStyle(null);
                this.filter = this.input.value;
                this.item = null;
                this.filterItems();
                flag = true;
                break;
            case 'Left':
            case 'ArrowLeft':
            case 'Right':
            case 'ArrowRight':
            case 'Home':
            case 'End':
                if (this.autocomplete === 'both') {
                    this.filter = this.input.value;
                }
                else {
                    this.item = null;
                    this.setCurrentOptionStyle(null);
                }
                this.setVisualFocusInput();
                flag = true;
                break;
            default:
                if (ComboboxWidget.isPrintableCharacter(char)) {
                    this.setVisualFocusInput();
                    this.setCurrentOptionStyle(null);
                    flag = true;
                    if (this.autocomplete === 'list' || this.autocomplete === 'both') {
                        item = this.filterItems();
                        if (item) {
                            if (this.isClosed() && this.input.value.length) {
                                this.open();
                            }
                            if (((_b = (_a = this.item) === null || _a === void 0 ? void 0 : _a.innerText.toLowerCase()) === null || _b === void 0 ? void 0 : _b.indexOf(this.input.value.toLowerCase())) === 0) {
                                this.item = item;
                                if (this.autocomplete === 'both' || this.listboxHasVisualFocus) {
                                    this.setCurrentOptionStyle(item);
                                    if (this.autocomplete === 'both') {
                                        this.setItem(item);
                                    }
                                }
                            }
                            else {
                                this.item = null;
                                this.setCurrentOptionStyle(null);
                            }
                        }
                        else {
                            this.close();
                            this.item = null;
                            this.setActiveDescendant(null);
                        }
                    }
                    else if (this.input.value.length) {
                        this.open();
                    }
                }
                break;
        }
        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
    handleInputClick() {
        if (this.isOpen()) {
            this.close(true);
        }
        else {
            this.open();
        }
    }
    handleInputFocus() {
        this.filter = this.input.value;
        this.filterItems();
        this.setVisualFocusInput();
        this.item = null;
        this.setCurrentOptionStyle(null);
    }
    handleInputBlur() {
        setTimeout(() => {
            this.removeVisualFocusAll();
        }, 500);
    }
    handleBackgroundPointerUp(event) {
        var _a;
        if (!this.input.contains(event.target) &&
            !this.listbox.contains(event.target) &&
            !((_a = this.button) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
            this.inputHasVisualFocus = false;
            this.setCurrentOptionStyle(null);
            this.removeVisualFocusAll();
            if (this.isOpen()) {
                setTimeout(this.close.bind(this, true), 300);
            }
        }
    }
    handleButtonClick() {
        if (this.isOpen()) {
            this.close(true);
        }
        else {
            this.open();
        }
        this.input.focus();
        this.setVisualFocusInput();
    }
    //  Listbox Events
    handleListboxPointerover() {
        this.hasHover = true;
    }
    handleListboxPointerout() {
        this.hasHover = false;
        setTimeout(this.close.bind(this, false), 300);
    }
    // Listbox Item Events
    handleItemClick(event) {
        // this.input.value = (event.target as HTMLElement).textContent;
        // this.close(true);
        this.setValue(event.target.textContent);
        this.close(true);
    }
    handleItemPointerover() {
        if (!this.closing) {
            this.hasHover = true;
            this.open();
        }
    }
    handleItemPointerout() {
        this.hasHover = false;
        setTimeout(this.close.bind(this, false), 300);
    }
}
